import React, { useEffect, useId, useRef, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeClasses: Record<ModalSize, string> = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-md',
  lg: 'md:max-w-lg',
  xl: 'md:max-w-xl',
  '2xl': 'md:max-w-2xl',
};

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter((element) => !element.hasAttribute('disabled'));
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
  /** Klasa dla warstwy overlay */
  overlayClassName?: string;
  /** Ukryj przycisk zamknięcia */
  hideClose?: boolean;
  /** Czy użytkownik może zamknąć modal przyciskiem, klawiszem Escape lub kliknięciem tła. */
  isDismissible?: boolean;
  /** Ukryj nagłówek modalu */
  hideHeader?: boolean;
  /** Etykieta dostępności dla modalu bez widocznego nagłówka */
  ariaLabel?: string;
  /** Element, który ma otrzymać fokus po otwarciu modalu (zamiast pierwszego fokusowalnego). */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Rozmiar modalu - predefiniowany (sm, md, lg, xl, 2xl) lub wartość w px */
  size?: ModalSize | number;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  hideClose = false,
  isDismissible = true,
  hideHeader = false,
  ariaLabel,
  initialFocusRef,
  className = '',
  overlayClassName = '',
  size = 'lg',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalFrameRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const timer = window.setTimeout(() => {
      const firstFocusableElement = getFocusableElements(
        modalFrameRef.current,
      )[0];
      (
        initialFocusRef?.current ??
        firstFocusableElement ??
        modalRef.current
      )?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [initialFocusRef, isOpen]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    const previousFocus = previousFocusRef.current;
    if (previousFocus?.isConnected) {
      previousFocus.focus();
    }
    previousFocusRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      if (event.key === 'Escape' && isDismissible) {
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(modalFrameRef.current);
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        modalRef.current?.focus();
        return;
      }

      if (!modalFrameRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        firstFocusableElement.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDismissible, isOpen, onClose]);

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (
      isDismissible &&
      modalFrameRef.current &&
      !modalFrameRef.current.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={clsx(
        'fixed inset-0 z-[100] flex items-center justify-center bg-black transition-all duration-300 ease-in-out',
        isAnimating ? 'bg-opacity-50' : 'bg-opacity-0',
        overlayClassName,
      )}
      onClick={handleOutsideClick}
      style={{ transition: 'background-color 300ms ease-in-out' }}
    >
      <div
        ref={modalFrameRef}
        className={clsx(
          'relative w-full transition-all duration-300 ease-in-out',
          hideHeader && !hideClose && isDismissible && 'pt-[30px]',
          typeof size === 'number' ? '' : sizeClasses[size],
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
        style={typeof size === 'number' ? { maxWidth: `${size}px` } : undefined}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel ?? (hideHeader ? title : undefined)}
          aria-labelledby={!hideHeader ? titleId : undefined}
          tabIndex={-1}
          className={clsx(
            'relative max-h-[90vh] w-full rounded-card bg-white p-6 shadow-lg',
            className,
          )}
        >
          {!hideHeader && (
            <div className="mb-4 flex items-center justify-between">
              <h2 id={titleId} className="text-xl font-bold">
                {title}
              </h2>
              {!hideClose && isDismissible && (
                <button
                  onClick={onClose}
                  className={clsx(
                    'flex items-center justify-center rounded-full p-1 text-2xl leading-none transition-colors',
                  )}
                  aria-label="Zamknij"
                >
                  &times;
                </button>
              )}
            </div>
          )}
          <div className="modal-content">{children}</div>
        </div>
        {hideHeader && !hideClose && isDismissible && (
          <button
            onClick={onClose}
            className={clsx(
              'absolute right-[-10px] top-0 z-10 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-primary text-2xl leading-none text-white shadow-feedback transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 desktop:-right-[30px]',
            )}
            aria-label="Zamknij"
          >
            &times;
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
